import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { 
  ProcurementDiscrepancy, 
  DiscrepancyStatus, 
  UpdateDiscrepancyInput 
} from '@/types/database/procurement-discrepancies';
import { updateDiscrepancyStatus, getPendingDiscrepancyStats } from '@/services/discrepancyService';
import { resolveSourceNames, getSourceName } from '@/services/discrepancySourceResolver';

interface DiscrepancyFilters {
  status?: DiscrepancyStatus | 'all';
  type?: 'overpayment' | 'underpayment' | 'all';
  dateFrom?: string;
  dateTo?: string;
}

export function usePaymentDiscrepancies(filters: DiscrepancyFilters = {}) {
  return useQuery({
    queryKey: ['payment-discrepancies', filters],
    queryFn: async (): Promise<ProcurementDiscrepancy[]> => {
      let query = supabase
        .from('procurement_discrepancies')
        .select(`
          *,
          procurements(
            id,
            product_name,
            total_quantity,
            created_at,
            source_type,
            source_id
          ),
          user_profiles!procurement_discrepancies_resolved_by_fkey(
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false });

      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters.type && filters.type !== 'all') {
        query = query.eq('discrepancy_type', filters.type);
      }

      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }

      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching discrepancies:', error);
        throw error;
      }

      const rows = (data || []) as any[];
      const refs = rows
        .map((r) => r.procurements)
        .filter(Boolean)
        .map((p: any) => ({ source_type: p.source_type, source_id: p.source_id }));
      const sourceMap = await resolveSourceNames(refs);

      return rows.map((item: any) => {
        const proc = item.procurements;
        const name = proc
          ? getSourceName(sourceMap, { source_type: proc.source_type, source_id: proc.source_id })
          : null;
        const sourceObj = name ? { name } : null;
        return {
          ...item,
          procurement: proc
            ? {
                id: proc.id,
                product_name: proc.product_name,
                total_quantity: proc.total_quantity,
                created_at: proc.created_at,
                agent: proc.source_type === 'agent' ? sourceObj : null,
                supplier: proc.source_type === 'supplier' ? sourceObj : null,
              }
            : undefined,
          resolver: item.user_profiles,
        };
      }) as ProcurementDiscrepancy[];
    },
  });
}

export function useDiscrepancy(id: string) {
  return useQuery({
    queryKey: ['payment-discrepancy', id],
    queryFn: async (): Promise<ProcurementDiscrepancy | null> => {
      const { data, error } = await supabase
        .from('procurement_discrepancies')
        .select(`
          *,
          procurements(
            id,
            product_name,
            total_quantity,
            total_amount,
            created_at,
            source_type,
            source_id,
            procurement_items(
              id,
              product_name,
              quantity_kg,
              price_per_unit,
              procurement_item_warehouse(received_quantity_kg, net_payable_kg),
              procurement_item_quality(compensation_kg)
            )
          ),
          user_profiles!procurement_discrepancies_resolved_by_fkey(
            full_name,
            email
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching discrepancy:', error);
        throw error;
      }

      if (!data) return null;

      const proc: any = (data as any).procurements;
      let agent: any = null;
      let supplier: any = null;
      if (proc?.source_type && proc?.source_id) {
        const sourceMap = await resolveSourceNames([
          { source_type: proc.source_type, source_id: proc.source_id },
        ]);
        const name = getSourceName(sourceMap, {
          source_type: proc.source_type,
          source_id: proc.source_id,
        });
        if (name) {
          if (proc.source_type === 'agent') agent = { name };
          else if (proc.source_type === 'supplier') supplier = { name };
        }
      }

      return {
        ...data,
        procurement: proc
          ? {
              id: proc.id,
              product_name: proc.product_name,
              total_quantity: proc.total_quantity,
              created_at: proc.created_at,
              agent,
              supplier,
            }
          : undefined,
        resolver: data.user_profiles as any,
      } as ProcurementDiscrepancy;
    },
    enabled: !!id,
  });
}

export function usePendingDiscrepancyStats() {
  return useQuery({
    queryKey: ['pending-discrepancy-stats'],
    queryFn: getPendingDiscrepancyStats,
    refetchInterval: 60000, // Refresh every minute
    refetchIntervalInBackground: false,
  });
}

export function useUpdateDiscrepancyStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      discrepancyId, 
      updates 
    }: { 
      discrepancyId: string; 
      updates: UpdateDiscrepancyInput 
    }) => {
      // If resolving/waiving, add resolver info
      if (updates.status === 'resolved' || updates.status === 'waived') {
        updates.resolved_by = user?.id;
        updates.resolved_at = new Date().toISOString();
      }

      return updateDiscrepancyStatus(discrepancyId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-discrepancies'] });
      queryClient.invalidateQueries({ queryKey: ['pending-discrepancy-stats'] });
      toast.success('Discrepancy updated successfully');
    },
    onError: (error: Error) => {
      console.error('Error updating discrepancy:', error);
      toast.error(`Failed to update discrepancy: ${error.message}`);
    },
  });
}

export function useMarkDiscrepancyReviewed() {
  const updateStatus = useUpdateDiscrepancyStatus();

  return useMutation({
    mutationFn: async (discrepancyId: string) => {
      return updateStatus.mutateAsync({
        discrepancyId,
        updates: { status: 'reviewed' }
      });
    },
  });
}

export function useResolveDiscrepancy() {
  const updateStatus = useUpdateDiscrepancyStatus();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      discrepancyId, 
      resolutionType, 
      resolutionNotes 
    }: { 
      discrepancyId: string; 
      resolutionType: string;
      resolutionNotes: string;
    }) => {
      return updateStatus.mutateAsync({
        discrepancyId,
        updates: {
          status: resolutionType === 'waived' ? 'waived' : 'resolved',
          resolution_type: resolutionType as any,
          resolution_notes: resolutionNotes,
          resolved_by: user?.id,
          resolved_at: new Date().toISOString(),
        }
      });
    },
  });
}
